import './data-quality.scss';

import { DataQualityService } from '@amzn/awscat-portfolio-assessment-service-client';
import { DataQualityGetRule, RuleStatus, ValidationSummary } from '@amzn/awscat-portfolio-assessment-types';
import { FlashContextInterface, FlashType, withFlashContext } from '@amzn/awscat-react-components';
import {
    Alert,
    Box,
    Button,
    ButtonDropdown,
    ButtonDropdownProps,
    Checkbox,
    CheckboxProps,
    Container,
    ExpandableSection,
    Grid,
    Icon,
    NonCancelableCustomEvent,
    Select,
    SelectProps,
    SpaceBetween,
    Spinner
} from '@amzn/awsui-components-react';
import { OptionDefinition } from '@amzn/awsui-components-react/polaris/internal/components/option/interfaces';
import { useCallback, useEffect, useState } from 'react';
import { match, useHistory, useRouteMatch } from 'react-router';
import { useIsMounted } from 'react-tidy';

import { DataQualityEnabledTypes, HelpPanelTopicOptions } from '../../common/Constants';
import { downloadFileFromUrl } from '../../common/DownloadService';
import { constants } from '../../common/Localization';
import { ErrorWithResponse, MatchParam } from '../../common/types';
import ConfirmationModal from '../common/ConfirmationModal';
import { InfoLink } from '../common/InfoLink';
import PreviewBanner from '../common/PreviewBanner';
import { actionItems, impactOptions, severityFilterAnyOption, severityFilterUnspecifiedOption, severityOptions } from './data-quality-constants';
import { 
    generateRulePluralStr,
    getAllRulesPassHeader,
    getDisabledRulesLabel,
    getPassingRulesLabel,
    getRulesHasLoadingErrorDescription,
    getRulesHasValidationErrorsHeader,
    sortImpacts,
} from './data-quality-helper';
import DataQualityCopyRulesModal from './DataQualityCopyRulesModal';
import DataQualityRuleValidationListItem from './DataQualityRuleValidationListItem';

const dataQualityService = new DataQualityService();

export interface RuleToDisplay extends DataQualityGetRule {
    selected: boolean;
    errorMsg?: string;
    warningMsg?: string;
}

interface Props<T> {
    assetType: DataQualityEnabledTypes;
    assetItems: T;
    reloadImmediately: boolean;
    filterOnRule: (ruleId: string) => void;
}

function DataQualityRuleValidation<T>({assetType, assetItems, reloadImmediately, filterOnRule, flash}: Props<T> & FlashContextInterface) {
    const { alert } = flash;
    const match: match<MatchParam> | null = useRouteMatch({
        path: '/portfolio/:portfolioId',
    });
    const portfolioId = match?.params?.portfolioId;
    const history = useHistory();
   
    const [loading, setLoading] = useState(false);
    const [downloadLoading, setDownloadLoading] = useState(false);
    const [deleteLoading, setDeleteLoading] = useState(false);
    const [rules, setRules] = useState<RuleToDisplay[]>([]);
    const [filteredRules, setFilteredRules] = useState<RuleToDisplay[]>([]);
    const [validationSummary, setValidationSummary] = useState<ValidationSummary>({rules: {}});
    const [areAllRulesSelected, setAreAllRulesSelected] = useState(false);
    const [showPassingRules, setShowPassingRules] = useState(false);
    const [showDisabledRules, setShowDisabledRules] = useState(false);
    const [selectedSeverityFilterOption, setSelectedSeverityFilterOption] = 
        useState<OptionDefinition>(severityFilterAnyOption);
    const [showSummaryRefreshButton, setShowSummaryRefreshButton] = useState(false);
    const [deleteConfirmationVisible, setDeleteConfirmationVisible] = useState(false);
    const [rulesToDelete, setRulesToDelete] = useState<RuleToDisplay[]>([]);
    const [copyRulesModalVisible, setCopyRulesModalVisible] = useState(false);

    const severityFilterOptions = severityOptions.slice();
    severityFilterOptions.unshift(severityFilterAnyOption);
    severityFilterOptions.push(severityFilterUnspecifiedOption);

    const isMounted = useIsMounted();

    const redirectToEdit = (ruleId: string) => (): void => {
        history.push(`/portfolio/${portfolioId}/management/assets/rules/edit/${ruleId}`);
    };

    const redirectToCreate = () => {
        history.push(`/portfolio/${portfolioId}/management/assets/rules/create`);
    };

    const loadValidationSummary = useCallback( async () => {
        try {
            if (portfolioId) {
                const summary: ValidationSummary = await dataQualityService.getValidationSummary(portfolioId, assetType);
                if (isMounted()) {
                    setValidationSummary(summary);
                }
            }
        } catch (err) {
            if (isMounted()) {
                const message = (err as ErrorWithResponse).response?.data?.statusCode == 404 ?
                    constants.ERROR_LOADING_VALIDATION_SUMMARY_NOT_FOUND :
                    (err as Error).message;
                alert(FlashType.error, message, constants.ERROR_LOADING_VALIDATION_SUMMARY);
            }
        }
    }, [ assetType, alert, isMounted, portfolioId ]);

    const loadRules = useCallback( async () => {
        setLoading(true);
        try{
            if (portfolioId) {
                const rules: DataQualityGetRule[] = await dataQualityService.listRules(portfolioId, assetType);
                if (!isMounted()) {
                    return;
                }
                const rulesToDisplay: RuleToDisplay[] = rules.map(rule => {
                    const displayRule: RuleToDisplay = Object.assign({selected: false}, rule);
                    displayRule.impacts?.sort(sortImpacts);
                    return displayRule;
                });
                setRules(rulesToDisplay);
                setFilteredRules(rulesToDisplay);

                const isRevalidationRequired = await dataQualityService.isRevalidationRequired(portfolioId, assetType);
                if (!isMounted()) {
                    return;
                }
                if (isRevalidationRequired) {
                    const summary = await dataQualityService.validateData(portfolioId, assetType);
                    if (isMounted()) {
                        setValidationSummary(summary);
                    }
                } else {
                    await loadValidationSummary();
                }
                if (isMounted()) {
                    setShowSummaryRefreshButton(false);
                }
            }
        } catch (err) {
            if (isMounted()) {
                setShowSummaryRefreshButton(true);
                flash.alert(FlashType.error,  (err as Error).message, constants.ERROR_LOADING_RULES);
            }
        } finally {
            if (isMounted()) {
                setLoading(false);
            }
        }
    }, [ assetType, flash, isMounted, loadValidationSummary, portfolioId ]);

    useEffect(() => {
        if (reloadImmediately) {
            loadRules();
        } else {
            setShowSummaryRefreshButton(true);
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [ assetItems ]); // TODO: find dependency that doesn't cause double load on initialization

    useEffect(() => {
        const populatedFilteredRules = () => {
            if (selectedSeverityFilterOption.value === severityFilterAnyOption.value) {
                setFilteredRules(rules);
            } else if (selectedSeverityFilterOption.value === severityFilterUnspecifiedOption.value) {
                setFilteredRules(rules.filter(rule => (rule.impacts?.length || 0) < impactOptions.length));
            } else {
                setFilteredRules(rules.filter(rule => rule.impacts?.some(impact => impact.severity === selectedSeverityFilterOption.value)));
            }
        };

        populatedFilteredRules();
    }, [ rules, selectedSeverityFilterOption ]);

    useEffect(() => {
        const deleteRuleAction = actionItems.find(action => action.id === 'deleteRule');
        if (deleteRuleAction) {
            const numRulesSelected = rules.filter(rule => rule.selected).length;
            const isDefaultRulesSelected = rules.find(rule => rule.selected && rule.isDefault);

            deleteRuleAction.disabled = numRulesSelected === 0 || isDefaultRulesSelected !== undefined;
            deleteRuleAction.text = rules.filter(rule => rule.selected && !rule.isDefault).length > 1 ?
                constants.DELETE_RULES : constants.DELETE_RULE;
        }

        const numActiveSelectedRules = rules.filter(rule => rule.selected && rule.status === RuleStatus.ACTIVE).length;
        const numInactiveSelectedRules = rules.filter(rule => rule.selected && rule.status === RuleStatus.INACTIVE).length;
        const disabledRuleAction = actionItems.find(action => action.id === 'disableRules');
        if (disabledRuleAction) {
            disabledRuleAction.disabled = !(numActiveSelectedRules > 0 && numInactiveSelectedRules === 0);
            disabledRuleAction.text = numActiveSelectedRules > 1 ? constants.DISABLE_RULES : constants.DISABLE_RULE;
        }

        const enableRuleAction = actionItems.find(action => action.id === 'enableRules');
        if (enableRuleAction) {
            enableRuleAction.disabled = !(numInactiveSelectedRules > 0 && numActiveSelectedRules === 0);
            enableRuleAction.text = numInactiveSelectedRules > 1 ? constants.ENABLE_RULES : constants.ENABLE_RULE;
        }

    }, [ rules, areAllRulesSelected ]);
    
    const onSummaryRefreshButtonClicked = async () => {
        await loadRules();
    };

    const actionTaken = (event: NonCancelableCustomEvent<ButtonDropdownProps.ItemClickDetails>) => {
        switch (event.detail.id) {
            case 'addNewRule':
                redirectToCreate();
                break;
            case 'deleteRule':
                confirmDeleteRule();
                break;
            case 'copyRules':
                setCopyRulesModalVisible(true);
                break;
            case 'disableRules':
                enableOrDisableRules(false);
                break;
            case 'enableRules':
                enableOrDisableRules(true);
                break;
            case 'download':
                downloadValidationResults();
                break;
            case 'revalidate':
                revalidateRules();
                break;
        }
    };

    const confirmDeleteRule = () => {
        const selectedRules = rules.filter(rule => rule.selected && !rule.isDefault);
        if (!selectedRules) {
            return;
        }

        setRulesToDelete(selectedRules);
        setDeleteConfirmationVisible(true);
    };

    const onDeleteRuleConfirmed = async () => {
        if (!portfolioId || rulesToDelete.length === 0) {
            return;
        }
        setDeleteLoading(true);
        const ruleIds = rulesToDelete.map(rule => rule.ruleId);

        try {
            await dataQualityService.deleteRules(portfolioId, ruleIds);
            flash.alert(FlashType.success, `${constants.SUCCESS_DELETING_RULE}${generateRulePluralStr(ruleIds.length)}`, constants.SUCCESS);
        } catch (err) {
            flash.alert(FlashType.error, (err as Error).message, `${constants.ERROR_DELETING_RULE}${generateRulePluralStr(ruleIds.length)}`);
        }

        setRulesToDelete([]);
        setDeleteLoading(false);
        setDeleteConfirmationVisible(false);
        
        // Reload rules
        setRules([]);
        setFilteredRules([]);
        setAreAllRulesSelected(false);
        loadRules();
    };

    const onCopyComplete = () => {
        loadRules();
    };

    const downloadValidationResults = async () => {
        if (!portfolioId) {
            return;
        }

        setDownloadLoading(true);
        try {
            const url = await dataQualityService.downloadReport(portfolioId, assetType);
            downloadFileFromUrl(url);
        } catch (err) {
            flash.alert(FlashType.error, (err as Error).message, '');
        } finally {
            setDownloadLoading(false);
        }
    };

    const enableOrDisableRules = async (isEnablingRules: boolean) => {
        if (!portfolioId) {
            return;
        }
        const currentStatus = isEnablingRules ? RuleStatus.INACTIVE : RuleStatus.ACTIVE;
        const desiredStatus = isEnablingRules ? RuleStatus.ACTIVE : RuleStatus.INACTIVE;

        const selectedRuleIds = rules.filter(rule => rule.selected && rule.status === currentStatus)
            .map(rule => rule.ruleId);
        setLoading(true);
        try {
            await dataQualityService.batchUpdateRules(portfolioId, selectedRuleIds, { status: desiredStatus });
            await loadRules();
        } catch (err) {
            const errorMessage = isEnablingRules ? constants.ERROR_ENABLING_RULES : constants.ERROR_DISABLING_RULES;
            flash.alert(FlashType.error, (err as Error).message, errorMessage);
        }
    };

    const revalidateRules = async () => {
        if (!portfolioId) {
            return;
        }
        setLoading(true);
        try {
            await dataQualityService.validateData(portfolioId, assetType);
            await loadValidationSummary();
        } catch (err) {
            flash.alert(FlashType.error, (err as Error).message, constants.ERROR_REVALIDATING_RULES);
        } finally {
            setLoading(false);
        }
    };

    const selectAllRulesChanged = (event: NonCancelableCustomEvent<CheckboxProps>): void => {
        rules.forEach(rule => rule.selected = event.detail.checked);
        setAreAllRulesSelected(event.detail.checked);
    };

    const ruleSelectedChanged = (rule: RuleToDisplay) => (event: NonCancelableCustomEvent<CheckboxProps.ChangeDetail>) => {
        const newRules = [...rules];
        const changedRule = newRules.find(r => r.ruleId === rule.ruleId);
        if (!changedRule) {
            return;
        }
        changedRule.selected = event.detail.checked;
        setRules(newRules);
    };

    const getPassingRulesCount = (): number => {
        const count = filteredRules.filter(rule => !(rule.ruleId in validationSummary.rules)).length - getDisabledRulesCount();
        return count || 0;
    };

    const getDisabledRulesCount = (): number => {
        return filteredRules.reduce((count: number, rule: RuleToDisplay) => {
            return rule.status === RuleStatus.INACTIVE ? count += 1 : count;
        }, 0);
    };

    const severityFilterChanged = (event: NonCancelableCustomEvent<SelectProps.ChangeDetail>): void => {
        setSelectedSeverityFilterOption(event.detail.selectedOption);
    };

    const togglePassingRules = (): void => {
        setShowPassingRules(!showPassingRules);
    };

    const toggleDisabledRules = (): void => {
        setShowDisabledRules(!showDisabledRules);
    };


    const ruleListHeaderRow = (
        <li className="validation-head" key="header">
            <div><Checkbox checked={areAllRulesSelected} onChange={selectAllRulesChanged}/></div>
            <div>{constants.VALIDATION_RULE}</div>
            {
                impactOptions.map(impact => <div key={impact.id}>{impact.label}</div>)
            }
            <div>{constants.TOTAL_ISSUES}</div>
        </li>
    );

    const showRule = (rule: RuleToDisplay): boolean => {
        if (!showPassingRules && !showDisabledRules) { // show enabled and failing only
            return rule.status === RuleStatus.ACTIVE && validationSummary.rules[rule.ruleId] > 0;
        } else if (!showDisabledRules && showPassingRules) { // show all enabled
            return rule.status === RuleStatus.ACTIVE;
        } else if (showDisabledRules && !showPassingRules) { // show all failing rules
            return (validationSummary.rules[rule.ruleId] > 0 && rule.status === RuleStatus.ACTIVE) 
                || rule.status === RuleStatus.INACTIVE;
        } else if (showPassingRules && showDisabledRules) { // show all
            return true;
        } else {
            return false;
        }
    };

    const getRuleListContent = () => {
        let ruleList;
        if (filteredRules.length === 0) {
            ruleList = constants.NO_RULES_FOUND;
        } else {
            ruleList = filteredRules.map(rule => {
                if (showRule(rule)) {
                    return (
                        <DataQualityRuleValidationListItem
                            key={rule.ruleId}
                            rule={rule}
                            ruleSelectedChanged={ruleSelectedChanged}
                            validationSummary={validationSummary}
                            editRule={() => redirectToEdit(rule.ruleId)}
                            filterInvalidRecords={filterOnRule}
                        />
                    );
                } else {
                    return null;
                }
            });
        }
        return (
            <div>
                {ruleList}
            </div>
        );
    };

    const ruleList = (
        <Box margin={{ top: 'm' }} className='rule-content'> 
            <ul className="validation-table">
                {ruleListHeaderRow}
                {getRuleListContent()}
            </ul>
        </Box>
    );

    const getExpandableSectionClassNames = (): string => {
        if (validationSummary.totalErrors === 0) {
            return 'data-validation-alert all-valid';
        } else {
            return 'data-validation-alert invalid';
        }
    };

    const getHeader = (): JSX.Element | null => {
        if (!validationSummary) {
            return null;
        }
        if (validationSummary.totalErrors === 0) {
            return (
                <Box data-testid="success">
                    <Icon name="status-positive" variant="success"/>
                    <Box margin={{left: 'xs'}} display='inline' variant='h4'>
                        {getAllRulesPassHeader(assetType)}
                    </Box>
                </Box>
            );
        } else if (validationSummary.totalErrors && validationSummary.totalErrors > 0) {
            return (
                <Box>
                    <Icon name="status-warning" variant="warning"/>
                    <Box margin={{left: 'xs'}} display='inline' variant='h4'>
                        {getRulesHasValidationErrorsHeader(validationSummary.totalErrors, assetType)}
                    </Box>
                </Box>
            );
        } else {
            return (
                <Box data-testid="error">
                    <Icon name="status-negative" variant="error" />
                    <Box margin={{left: 'xs'}} display='inline' variant='h4'>
                        {constants.ERROR_LOADING_HEADER}
                    </Box>
                </Box>
            );
        }
    };

    const getDescription = () => {
        if (!validationSummary) {
            return null;
        }
        if (validationSummary.totalErrors === 0) {
            return constants.NO_ERRORS_FOUND_DESCRIPTION;
        } else if (validationSummary.totalErrors && validationSummary.totalErrors > 0) {
            return constants.ERRORS_FOUND_DESCRIPTION;
        } else {
            return getRulesHasLoadingErrorDescription(rules.length);
        }
    }; 

    const severityFilter = (
        <SpaceBetween size="s" direction="horizontal">
            <Icon name="filter"/>
            <Select
                onChange={severityFilterChanged}
                options={severityFilterOptions} 
                selectedOption={selectedSeverityFilterOption}
                expandToViewport
            />
        </SpaceBetween>
    );

    const contentActionBar = (
        <Grid gridDefinition={[{colspan: 8}, {colspan: 4}, {colspan: 12}]} disableGutters>
            <Box padding={{ left: 'm' }} display='inline-block'>
                {getDescription()}
                <InfoLink className='data-quality-info-link' topic={HelpPanelTopicOptions.DATA_QUALITY} />
            </Box>
            <Box float="right" padding={{ right: 'm' }}>
                <SpaceBetween size='s' direction='horizontal'>
                    {showSummaryRefreshButton ? <Button iconName='refresh' onClick={onSummaryRefreshButtonClicked}>Refresh</Button> : null}
                    <ButtonDropdown 
                        items={actionItems}
                        onItemClick={actionTaken}
                        loading={downloadLoading}
                    >
                        Actions
                    </ButtonDropdown>
                </SpaceBetween>
            </Box>
            <Box padding={{ top: 'm', left: 'm' }}>
                {severityFilter}
            </Box>
        </Grid>
    );

    const getShowHideIcon = (isShowing: boolean) => {
        return isShowing ? (
            <Icon name='treeview-collapse' variant='link' size='medium' />
        ) : (
            <Icon name='treeview-expand' variant='link' size='medium' />
        );
    };

    const getFooter = () => {
        const passingRuleCount = getPassingRulesCount();
        const passingRuleStr = getPassingRulesLabel(showPassingRules, passingRuleCount);
        const passingRuleIcon = getShowHideIcon(showPassingRules);
        
        let passingRules;
        if (passingRuleCount > 0) {
            passingRules = (
                <div onClick={togglePassingRules}>
                    {passingRuleIcon} {passingRuleStr}
                </div>
            );
        }

        const disabledRulesCount = getDisabledRulesCount();
        const disabledRulesStr = getDisabledRulesLabel(showDisabledRules, disabledRulesCount);
        const disabledRuleIcon = getShowHideIcon(showDisabledRules);

        let disabledRules;
        if (disabledRulesCount > 0) {
            disabledRules = (
                <div onClick={toggleDisabledRules}>
                    {disabledRuleIcon} {disabledRulesStr}
                </div>
            );
        }

        return (
            <div className='data-quality-footer'>
                <Box padding={'m'}>
                    <SpaceBetween size="xxs">
                        {passingRules}
                        {disabledRules}
                    </SpaceBetween>
                </Box>
            </div>
        );

    };

    const content = (
        <ExpandableSection
            header={getHeader()}
            className={getExpandableSectionClassNames()}
            variant="footer"
        >
            {contentActionBar}
            {ruleList}
            {getFooter()}
        </ExpandableSection>
    );

    const previewBanner = (
        <PreviewBanner customHeader={constants.DATA_QUALITY_PREVIEW_HEADER} />
    );

    return loading ? (
        <SpaceBetween size='s'>
            {previewBanner}
            <Container>
                <Spinner size="large" />
            </Container>
        </SpaceBetween>
    ) : (
        <div>
            {previewBanner}
            <Container
                disableContentPaddings
                data-testid='container'
            >
                {content}
            </Container>
            {copyRulesModalVisible ? (
                <DataQualityCopyRulesModal
                    visible={copyRulesModalVisible}
                    setVisible={setCopyRulesModalVisible}
                    assetType={assetType}
                    currentRules={rules}
                    onCopyComplete={onCopyComplete}
                />
            ) : null}
            {deleteConfirmationVisible ? (
                <ConfirmationModal 
                    friction='delete'
                    visible
                    header={constants.DELETE_RULE + '?'}
                    warning=''
                    onConfirm={onDeleteRuleConfirmed}
                    confirmButton={constants.DELETE_BUTTON_TEXT}
                    confirmLoading={deleteLoading}
                    onDismiss={() => {setDeleteConfirmationVisible(false);}}
                    onCancel={() => {setDeleteConfirmationVisible(false);}}>
                    <Alert header={constants.THIS_ACTION_CANNOT_BE_UNDONE} dismissible={false} type='error'>
                        {constants.YOU_ARE_ABOUT_TO_DELETE}
                        <Box>
                            {rulesToDelete.map(rule => {
                                return (
                                    <div><strong>{rule.name}</strong></div>
                                );
                            })}
                        </Box>
                    </Alert>
                </ConfirmationModal>
            ) : null
            }
        </div>
    );
}

export default withFlashContext(DataQualityRuleValidation);
